import { Controller } from "@hotwired/stimulus"
import { favouritesKey } from "../lib/favourites_store"
import { showToast } from "../lib/toast"
import { ping } from "../lib/ping"
import { trackEvent } from "../lib/analytics"

const SPRING = "cubic-bezier(0.34, 1.56, 0.64, 1)"
const SETTLE = "cubic-bezier(0.16, 1, 0.3, 1)"

export default class extends Controller {
  static values = {
    weekSlug: String
  }

  static targets = ["heart", "card", "filterButton", "count", "emptyState", "banner", "saveAll"]

  connect() {
    this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.loadFavourites()
    this.updateUI()
  }

  // Hearts and Save all buttons that arrive later (plan mode swaps its walk in) need their state
  heartTargetConnected() {
    this.scheduleUpdate()
  }

  saveAllTargetConnected() {
    this.scheduleUpdate()
  }

  scheduleUpdate() {
    if (!this.favourites || this.updateScheduled) return

    this.updateScheduled = true
    requestAnimationFrame(() => {
      this.updateScheduled = false
      this.updateUI()
    })
  }

  getStorageKey() {
    return favouritesKey(this.weekSlugValue)
  }

  loadFavourites() {
    try {
      const stored = localStorage.getItem(this.getStorageKey())
      this.favourites = stored ? JSON.parse(stored) : []
    } catch (e) {
      this.favourites = []
    }
  }

  saveFavourites() {
    try {
      localStorage.setItem(this.getStorageKey(), JSON.stringify(this.favourites))
    } catch (e) {
      // Storage might be full or unavailable
    }
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    const eventId = parseInt(button.dataset.eventId)
    this.loadFavourites()
    const index = this.favourites.indexOf(eventId)
    const saving = index === -1

    if (saving) {
      this.favourites.push(eventId)
      showToast("Added to your favourites", { icon: "heart" })
      ping("favourite", { event_id: eventId })
      trackEvent("favourite", { event_id: eventId, source: "heart" }, button)
    } else {
      this.favourites.splice(index, 1)
      showToast("Removed from your favourites")
      ping("unfavourite", { event_id: eventId })
    }

    this.saveFavourites()
    this.updateUI()
    this.pop(button, saving)
  }

  // Plan mode's Add links for events that aren't favourites yet: favourite it, then let the link load the new walk
  add(event) {
    const eventId = parseInt(event.currentTarget.dataset.eventId)
    this.loadFavourites()
    if (this.isFavourite(eventId)) return

    this.favourites.push(eventId)
    this.saveFavourites()
    this.updateUI()
    showToast("Added to your favourites", { icon: "heart" })
    ping("favourite", { event_id: eventId })
    trackEvent("favourite", { event_id: eventId, source: "plan" }, event.currentTarget)
  }

  // Saves every stop of a walk, or unsaves them all if they're already saved
  saveAll(event) {
    event.preventDefault()

    const button = event.currentTarget
    const ids = [...new Set(this.idsFor(button))]
    this.loadFavourites()
    const saving = !ids.every(id => this.isFavourite(id))
    if (saving) {
      showToast("Added to your favourites", { icon: "heart" })
      ids.forEach(id => {
        if (this.isFavourite(id)) return
        this.favourites.push(id)
        ping("favourite", { event_id: id })
        trackEvent("favourite", { event_id: id, source: "save_all" }, button)
      })
    } else {
      ids.forEach(id => ping("unfavourite", { event_id: id }))
      this.favourites = this.favourites.filter(id => !ids.includes(id))
      showToast("Removed from your favourites")
    }

    this.saveFavourites()
    this.updateUI()
    this.pop(button, saving)
  }

  // The heart springs up a little when it fills, and dips when it empties
  pop(button, saving) {
    // A light tap of feedback on phones that support it
    if (saving) navigator.vibrate?.(8)

    const svg = button.querySelector("svg")
    if (!svg || this.reduceMotion) return

    svg.animate(saving
      ? [{ transform: "scale(1)" }, { transform: "scale(0.82)", offset: 0.25 }, { transform: "scale(1.16)", offset: 0.6 }, { transform: "scale(1)" }]
      : [{ transform: "scale(1)" }, { transform: "scale(0.86)", offset: 0.4 }, { transform: "scale(1)" }],
    { duration: saving ? 460 : 260, easing: saving ? SPRING : SETTLE })
  }

  idsFor(element) {
    return (element.dataset.eventIds || "").split(",").map(id => parseInt(id)).filter(Boolean)
  }

  isFavourite(eventId) {
    return this.favourites.includes(parseInt(eventId))
  }

  isFiltering() {
    const url = new URL(window.location.href)
    return url.searchParams.get("favourites") === "true"
  }

  toggleFilter(event) {
    event.preventDefault()

    const url = new URL(window.location.href)

    if (this.isFiltering()) {
      url.searchParams.delete("favourites")
    } else {
      // Clear other filters when enabling favourites
      url.searchParams.delete("day")
      url.searchParams.delete("type")
      url.searchParams.set("favourites", "true")
    }

    // Navigate to the new URL (works like filter links)
    window.location.href = url.toString()
  }

  updateUI() {
    const isFiltering = this.isFiltering()

    // Update all heart icons on cards
    this.heartTargets.forEach(heart => {
      const eventId = parseInt(heart.dataset.eventId)
      const isFav = this.isFavourite(eventId)
      heart.classList.toggle("text-red-500", isFav)
      heart.classList.toggle("text-stone-400", !isFav)
      heart.setAttribute("aria-pressed", isFav ? "true" : "false")

      const svg = heart.querySelector("svg")
      if (svg) {
        svg.setAttribute("fill", isFav ? "currentColor" : "none")
      }
    })

    this.saveAllTargets.forEach(button => {
      const ids = this.idsFor(button)
      const allSaved = ids.length > 0 && ids.every(id => this.isFavourite(id))
      button.setAttribute("aria-pressed", allSaved ? "true" : "false")
      button.querySelector("svg")?.setAttribute("fill", allSaved ? "currentColor" : "none")
      const unsaved = button.querySelector("[data-label-unsaved]")
      const saved = button.querySelector("[data-label-saved]")
      if (unsaved) unsaved.hidden = allSaved
      if (saved) saved.hidden = !allSaved
    })

    // Update filter button in header
    if (this.hasFilterButtonTarget) {
      this.filterButtonTarget.classList.toggle("text-red-500", isFiltering)
      const svg = this.filterButtonTarget.querySelector("svg")
      if (svg) {
        svg.setAttribute("fill", isFiltering ? "currentColor" : "none")
      }
    }

    if (this.hasCountTarget) this.showCount(this.favourites.length)

    // Filter cards visibility when favourites filter is active
    let visibleCount = 0
    if (isFiltering) {
      this.cardTargets.forEach(card => {
        const eventId = parseInt(card.dataset.eventId)
        const isVisible = this.isFavourite(eventId)
        card.style.display = isVisible ? "" : "none"
        if (isVisible) visibleCount++
      })

      // Also hide empty day sections
      document.querySelectorAll("[data-day-section]").forEach(section => {
        const visibleCards = section.querySelectorAll("[data-favourites-target='card']:not([style*='display: none'])")
        section.style.display = visibleCards.length > 0 ? "" : "none"
      })
    } else {
      this.cardTargets.forEach(card => {
        card.style.display = ""
      })
      document.querySelectorAll("[data-day-section]").forEach(section => {
        section.style.display = ""
      })
    }

    // Show empty state ONLY when filtering with no favourites at all (not just filtered out)
    if (this.hasEmptyStateTarget) {
      const showEmpty = isFiltering && this.favourites.length === 0
      this.emptyStateTarget.classList.toggle("hidden", !showEmpty)
    }

    // Show/hide favourites banner
    if (this.hasBannerTarget) {
      this.bannerTarget.classList.toggle("hidden", !isFiltering)
    }

    // Update filter links to preserve favourites param when in favourites mode
    this.updateFilterLinks(isFiltering)

    this.dispatch("changed", { detail: { count: this.favourites.length } })
  }

  // The Me tab's badge: it pops in at the first favourite, ticks up or down as the count changes
  // (the old number rolls away as the new one rolls in) and shrinks away at zero
  showCount(count) {
    const badge = this.countTarget
    const previous = this.shownCount
    this.shownCount = count
    if (previous === count) return

    const animate = previous !== undefined && !this.reduceMotion

    if (count === 0) {
      if (!animate) return void (badge.hidden = true)
      badge.animate([{ transform: "scale(1)", opacity: 1 }, { transform: "scale(0.4)", opacity: 0 }], { duration: 200, easing: "ease-in" })
        .finished.then(() => { if (this.shownCount === 0) badge.hidden = true }, () => {})
      return
    }

    const number = document.createElement("span")
    number.textContent = count

    if (!animate || badge.hidden || !previous) {
      badge.replaceChildren(number)
      badge.hidden = false
      if (animate) badge.animate([{ transform: "scale(0.4)", opacity: 0 }, { transform: "scale(1)", opacity: 1 }], { duration: 420, easing: SPRING })
      return
    }

    const up = count > previous
    const old = badge.firstElementChild
    badge.replaceChildren(number)
    if (old) {
      old.style.cssText = "position:absolute;inset:0;display:flex;align-items:center;justify-content:center"
      badge.append(old)
      old.animate([{ transform: "translateY(0)", opacity: 1 }, { transform: `translateY(${up ? -100 : 100}%)`, opacity: 0 }], { duration: 320, easing: SETTLE, fill: "forwards" })
        .finished.then(() => old.remove(), () => old.remove())
    }
    number.animate([{ transform: `translateY(${up ? 100 : -100}%)`, opacity: 0 }, { transform: "translateY(0)", opacity: 1 }], { duration: 320, easing: SETTLE })
    badge.animate([{ transform: "scale(1)" }, { transform: "scale(1.18)", offset: 0.35 }, { transform: "scale(1)" }], { duration: 420, easing: SETTLE })
  }

  updateFilterLinks(isFiltering) {
    // Find filter links in the filter sheet only (not more menu or other areas)
    const filterLinks = document.querySelectorAll("[data-bottom-sheet-target='filterSheet'] a")
    
    filterLinks.forEach(link => {
      const url = new URL(link.href, window.location.origin)
      
      if (isFiltering) {
        // Add favourites param to filter links when in favourites mode
        url.searchParams.set("favourites", "true")
      } else {
        // Remove favourites param when not in favourites mode
        url.searchParams.delete("favourites")
      }
      
      link.href = url.toString()
    })
  }
}
